import { useEffect, useState } from "react";
import api from "@/lib/api";
import { getCurrentPosition } from "@/lib/geolocation";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, MapPin, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import GeofenceMap from "@/components/GeofenceMap";

const EMPTY = {
  name: "", country_id: "", address: "",
  latitude: 0, longitude: 0, radius_meters: 100,
  status: "active", admin_ids: [],
  weekday_start_time: "09:00",
  weekday_end_time: "18:00",
  saturday_enabled: false,
  saturday_start_time: "09:00",
  saturday_end_time: "14:00",
};

export default function Centers() {
  const [items, setItems] = useState([]);
  const [countries, setCountries] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const [c, co, u] = await Promise.all([
      api.get("/centers"),
      api.get("/countries"),
      api.get("/users"),
    ]);
    setItems(c.data); setCountries(co.data);
    setAdmins(u.data.filter(x => x.role === "center_admin"));
  };
  useEffect(() => { load(); }, []);

  const country = (id) => countries.find(c => c.id === id)?.name || "—";
  const startCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (c) => {
    setEditing(c);
    setForm({
      ...EMPTY,
      ...c,
      // Ensure new fields have sane defaults when editing pre-existing centers
      weekday_start_time: c.weekday_start_time || "09:00",
      weekday_end_time: c.weekday_end_time || "18:00",
      saturday_enabled: !!c.saturday_enabled,
      saturday_start_time: c.saturday_start_time || "09:00",
      saturday_end_time: c.saturday_end_time || "14:00",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, latitude: parseFloat(form.latitude), longitude: parseFloat(form.longitude), radius_meters: parseFloat(form.radius_meters) };
      if (editing) await api.patch(`/centers/${editing.id}`, payload);
      else await api.post("/centers", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this center?")) return;
    await api.delete(`/centers/${id}`); toast.success("Deleted"); load();
  };

  const useHere = async () => {
    try {
      const pos = await getCurrentPosition();
      setForm(f => ({ ...f, latitude: pos.latitude, longitude: pos.longitude }));
    } catch (e) {
      toast.error(e?.message || "Location permission denied");
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Geofences"
        title="Office Centers"
        subtitle="Configure GPS-based attendance zones"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-center-btn" onClick={startCreate} className="rounded-none btn-primary-brand">
                <Plus className="h-4 w-4 mr-1" /> New center
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display">{editing ? "Edit center" : "New center"}</DialogTitle></DialogHeader>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                <div>
                  <Label className="label-mono">Name</Label>
                  <Input data-testid="center-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" />
                </div>
                <div>
                  <Label className="label-mono">Country</Label>
                  <Select value={form.country_id} onValueChange={(v) => setForm({ ...form, country_id: v })}>
                    <SelectTrigger data-testid="center-country-select" className="rounded-none"><SelectValue placeholder="Choose country" /></SelectTrigger>
                    <SelectContent className="rounded-none bg-card">
                      {countries.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-mono">Address</Label>
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-none" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="label-mono">Latitude</Label>
                    <Input data-testid="center-lat-input" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} className="rounded-none font-mono-brand" />
                  </div>
                  <div>
                    <Label className="label-mono">Longitude</Label>
                    <Input data-testid="center-lng-input" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} className="rounded-none font-mono-brand" />
                  </div>
                  <div>
                    <Label className="label-mono">Radius (m)</Label>
                    <Input data-testid="center-radius-input" type="number" value={form.radius_meters} onChange={(e) => setForm({ ...form, radius_meters: e.target.value })} className="rounded-none font-mono-brand" />
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <button type="button" onClick={useHere} className="text-xs label-mono underline underline-offset-4">Use my location</button>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="rounded-none w-32"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none bg-card">
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-mono">Center Admins</Label>
                  <div className="max-h-32 overflow-auto border border-border p-2 space-y-1">
                    {admins.map(a => (
                      <label key={a.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={form.admin_ids.includes(a.id)}
                          onChange={(e) => {
                            const list = new Set(form.admin_ids);
                            e.target.checked ? list.add(a.id) : list.delete(a.id);
                            setForm({ ...form, admin_ids: Array.from(list) });
                          }}
                        />
                        {a.name} <span className="label-mono">{a.employee_id}</span>
                      </label>
                    ))}
                    {admins.length === 0 && <div className="text-xs text-muted-foreground">No center admins yet.</div>}
                  </div>
                </div>

                {/* ---- Per-center working hours (local time) ---- */}
                <div className="border border-border p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="label-mono">Working hours · local time</Label>
                    <span className="text-[10px] text-muted-foreground">Overrides global policy</span>
                  </div>
                  <div>
                    <div className="label-mono mb-1">Mon – Fri</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Morning start</Label>
                        <Input
                          data-testid="center-weekday-start"
                          type="time"
                          value={form.weekday_start_time}
                          onChange={(e) => setForm({ ...form, weekday_start_time: e.target.value })}
                          className="rounded-none font-mono-brand"
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Evening end</Label>
                        <Input
                          data-testid="center-weekday-end"
                          type="time"
                          value={form.weekday_end_time}
                          onChange={(e) => setForm({ ...form, weekday_end_time: e.target.value })}
                          className="rounded-none font-mono-brand"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        data-testid="center-saturday-toggle"
                        type="checkbox"
                        checked={form.saturday_enabled}
                        onChange={(e) => setForm({ ...form, saturday_enabled: e.target.checked })}
                      />
                      <span className="label-mono">Saturday is a working day</span>
                    </label>
                    {form.saturday_enabled && (
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div>
                          <Label className="text-[10px] text-muted-foreground">Saturday start</Label>
                          <Input
                            data-testid="center-saturday-start"
                            type="time"
                            value={form.saturday_start_time}
                            onChange={(e) => setForm({ ...form, saturday_start_time: e.target.value })}
                            className="rounded-none font-mono-brand"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] text-muted-foreground">Saturday end</Label>
                          <Input
                            data-testid="center-saturday-end"
                            type="time"
                            value={form.saturday_end_time}
                            onChange={(e) => setForm({ ...form, saturday_end_time: e.target.value })}
                            className="rounded-none font-mono-brand"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                </div>

                <div className="space-y-2">
                  <Label className="label-mono">Geofence preview · click map or drag pin</Label>
                  <GeofenceMap
                    latitude={form.latitude}
                    longitude={form.longitude}
                    radius={form.radius_meters}
                    onPick={(lat, lng) => setForm(f => ({ ...f, latitude: lat, longitude: lng }))}
                  />
                  <div className="label-mono">
                    {Number(form.latitude || 0).toFixed(5)}, {Number(form.longitude || 0).toFixed(5)} · {form.radius_meters}m radius
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="center-save-btn" onClick={save} className="rounded-none btn-primary-brand">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(c => (
          <Card key={c.id} className="rounded-none border-border p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="label-mono">{country(c.country_id)}</div>
                <div className="font-display text-lg mt-1">{c.name}</div>
                <div className="text-sm text-muted-foreground">{c.address}</div>
              </div>
              <MapPin className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-border">
              <div>
                <div className="label-mono">Lat</div>
                <div className="font-mono-brand text-sm">{Number(c.latitude).toFixed(4)}</div>
              </div>
              <div>
                <div className="label-mono">Lng</div>
                <div className="font-mono-brand text-sm">{Number(c.longitude).toFixed(4)}</div>
              </div>
              <div>
                <div className="label-mono">Radius</div>
                <div className="font-mono-brand text-sm">{c.radius_meters}m</div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-border text-xs">
              <div className="label-mono mb-1">Working hours</div>
              <div className="font-mono-brand">
                Mon–Fri {c.weekday_start_time || "09:00"} – {c.weekday_end_time || "18:00"}
              </div>
              <div className={`font-mono-brand ${c.saturday_enabled ? "" : "text-muted-foreground"}`}>
                Sat {c.saturday_enabled
                  ? `${c.saturday_start_time || "09:00"} – ${c.saturday_end_time || "14:00"}`
                  : "off"}
              </div>
            </div>
            <div className="flex justify-end gap-1 mt-3">
              <button onClick={() => startEdit(c)} className="p-2 border border-border hover:bg-secondary"><Pencil className="h-3.5 w-3.5" strokeWidth={1.5} /></button>
              <button onClick={() => del(c.id)} className="p-2 border border-border hover:bg-secondary"><Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} /></button>
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}
